import { v4 as uuid } from 'uuid';
import { LeaseSyncClientInterface } from './Interfaces/LSS/LeaseSyncClientInterface';
import { FilePathRequestV1 } from './Interfaces/LSS/FilePathRequestV1';
import { FilePathRequestV2 } from './Interfaces/LSS/FilePathRequestV2';
import { GetFileReplyV1 } from './Interfaces/LSS/GetFileReplyV1';
import { GetFileReplyV2 } from './Interfaces/LSS/GetFileReplyV2';
import { GetMetadataReplyV1 } from './Interfaces/LSS/GetMetadataReplyV1';
import { TaskContextInterface } from './Interfaces/CWA/TaskContextInterface';
import { callAsyncNative } from './NativeFunctions';
import { EndpointNotFoundError, HttpRequestError } from './Errors';
import {
	GET_ROOT_METADATA,
	POST_METHOD,
	GET_CHILDREN_METADATA,
	GET_FILE,
	CUSTOMER_ID_HEADER,
	CONTENT_TYPE_HEADER,
	ContentTypes,
	TRANSACTION_ID_HEADER,
	StatusCodes,
	CORRELATION_ID_HEADER,
	IGNORE_AUTH_HEADER,
	GET_FILES,
} from './Constants';
import { LeaseSyncEndpoints } from './Interfaces/CWA/LeaseSyncEndpoints';
import { Helper } from './Helper';

export class ApiGatewayClient implements LeaseSyncClientInterface {
	private context: TaskContextInterface;
	public constructor(context: TaskContextInterface) {
		this.context = context;
	}

	/**
	 * Sets the context of the client
	 * @param context
	 */
	public setContext(context: TaskContextInterface): void {
		this.context = context;
	}

	/**
	 * Request the metadata of the root file or folder from CLIS
	 * @param request - The request object to be passed as a parameter
	 */
	public async getRootMetadata(
		request: FilePathRequestV1,
		correlationId: string
	): Promise<GetMetadataReplyV1> {
		const response = await this.invokeAjax(
			request,
			GET_ROOT_METADATA,
			POST_METHOD,
			correlationId,
			1
		);
		return response as GetMetadataReplyV1;
	}

	/**
	 * Request the metadata of the root folder's children from CLIS
	 * @param request - The request object to be passed as a parameter
	 */
	public async getChildrenMetadata(
		request: FilePathRequestV1,
		correlationId: string
	): Promise<GetMetadataReplyV1> {
		const response = await this.invokeAjax(
			request,
			GET_CHILDREN_METADATA,
			POST_METHOD,
			correlationId,
			1
		);
		return response as GetMetadataReplyV1;
	}

	/**
	 * Request the metadata and contents of a file from CLIS
	 * @param request - The request object to be passed as a parameter
	 */
	public async getFile(
		request: FilePathRequestV1,
		correlationId: string
	): Promise<GetFileReplyV1> {
		const response = await this.invokeAjax(request, GET_FILE, POST_METHOD, correlationId);
		return response as GetFileReplyV1;
	}

	public async getFiles(
		request: FilePathRequestV2,
		correlationId: string
	): Promise<GetFileReplyV2> {
		const response = await this.invokeAjax(request, GET_FILES, POST_METHOD, correlationId);
		return response as GetFileReplyV2;
	}

	private async invokeAjax(
		request: FilePathRequestV1 | FilePathRequestV2,
		endpoint: keyof LeaseSyncEndpoints,
		method: string,
		correlationId: string,
		numRetries = 0
	): Promise<GetMetadataReplyV1 | GetFileReplyV1 | GetFileReplyV2> {
		if (!this.context || !this.context.endpoints || !this.context.endpoints[endpoint])
			throw new EndpointNotFoundError(endpoint, StatusCodes.EndpointNotFound);
		const uri = this.context.endpoints[endpoint];
		const packedHeaders = this.getPackedHeaders(correlationId);
		let maxRequests = numRetries + 1;
		let data = ""; 
		let statusCode = 0;
		while (maxRequests > 0) {
			[statusCode, data] = await callAsyncNative<[number, string]>(
				'ajax',
				(httpStatus: number, httpResult: string) => [httpStatus, httpResult],
				method,
				uri,
				packedHeaders,
				Helper.stringifyJSON(request),
				'',
				this.context.userId,
				this.context.storeGuid
			);
			if (statusCode == StatusCodes.OK) {
				const resultAsJson = Helper.parseJSON(data);
				return resultAsJson;
			} else if (statusCode != StatusCodes.ServiceUnavailable) {
				throw new HttpRequestError(uri, method, statusCode, data);
			}
			maxRequests--;
		}
		throw new HttpRequestError(uri, method, statusCode, data);
	}

	private getPackedHeaders(correlationId: string): string {
		const headers = [
			`${CUSTOMER_ID_HEADER}: ${this.context.customerId}`,
			`${CONTENT_TYPE_HEADER}: ${ContentTypes.JSON}`,
			`${TRANSACTION_ID_HEADER}: ${uuid()}`,
			`${IGNORE_AUTH_HEADER}: True`,
		];

		if (correlationId != null) {
			headers.push(`${CORRELATION_ID_HEADER}: ${correlationId}`);
		}

		return headers.join('\n');
	}
}
